import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  CheckCircleIcon,
  InfoCircleIcon,
  XCircleIcon,
  XCloseIcon,
} from "components";
import { error, gray, primary, success, warning } from "configs/colors";
import { CustomContentProps, SnackbarContent, useSnackbar } from "notistack";

// ─── Variant config ───────────────────────────────────────────────────────────

type VariantConfig = {
  accentColor: string;
  bgColor: string;
  iconColor: string;
  icon: React.ReactNode;
  label: string;
};

const VARIANT_CONFIGS: Record<string, VariantConfig> = {
  default: {
    accentColor: primary[700],
    bgColor: "#FFFFFF",
    iconColor: primary[600],
    icon: <InfoCircleIcon sx={{ fontSize: 20 }} />,
    label: "Thông báo",
  },
  success: {
    accentColor: success[600],
    bgColor: success[25],
    iconColor: success[600],
    icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
    label: "Thành công",
  },
  error: {
    accentColor: error[600],
    bgColor: error[25],
    iconColor: error[600],
    icon: <XCircleIcon sx={{ fontSize: 20 }} />,
    label: "Lỗi",
  },
  warning: {
    accentColor: warning[600],
    bgColor: warning[50],
    iconColor: warning[600],
    icon: <InfoCircleIcon sx={{ fontSize: 20 }} />,
    label: "Cảnh báo",
  },
  info: {
    accentColor: primary[600],
    bgColor: primary[25],
    iconColor: primary[600],
    icon: <InfoCircleIcon sx={{ fontSize: 20 }} />,
    label: "Thông tin",
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export const NotificationDefault = forwardRef<
  HTMLDivElement,
  CustomContentProps
>(({ id, variant, ...props }, ref) => {
  const { closeSnackbar } = useSnackbar();
  const progressRef = useRef<HTMLDivElement>(null);

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  const config = useMemo(
    () => VARIANT_CONFIGS[variant] ?? VARIANT_CONFIGS.default,
    [variant]
  );

  // Animate progress bar countdown
  useEffect(() => {
    const el = progressRef.current;
    if (!el) return;
    // Trigger reflow then start transition
    el.style.transition = "none";
    el.style.width = "100%";
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetWidth; // force reflow
    el.style.transition = "width 3s linear";
    el.style.width = "0%";
  }, []);

  return (
    <SnackbarContent ref={ref}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: 340,
          background: config.bgColor,
          borderRadius: "12px",
          borderLeft: `4px solid ${config.accentColor}`,
          boxShadow:
            "0px 8px 24px rgba(0,0,0,0.10), 0px 2px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Main content */}
        <Stack
          direction="row"
          alignItems="flex-start"
          gap="10px"
          p="14px 14px 12px 14px"
        >
          {/* Icon */}
          <Box
            sx={{
              color: config.iconColor,
              flexShrink: 0,
              mt: "1px",
              display: "flex",
            }}
          >
            {config.icon}
          </Box>

          {/* Text */}
          <Stack flex={1} gap="2px" minWidth={0}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "18px",
                color: gray[900],
                letterSpacing: "-0.1px",
              }}
            >
              {config.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: "18px",
                color: gray[600],
                wordBreak: "break-word",
              }}
            >
              {props.message}
            </Typography>
          </Stack>

          {/* Close button */}
          <IconButton
            onClick={handleDismiss}
            size="small"
            sx={{
              flexShrink: 0,
              mt: "-4px",
              mr: "-4px",
              color: gray[400],
              padding: "4px",
              borderRadius: "6px",
              "&:hover": {
                background: "rgba(0,0,0,0.05)",
                color: gray[600],
              },
            }}
          >
            <XCloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>

        {/* Progress bar */}
        <Box
          ref={progressRef}
          sx={{
            height: "3px",
            background: config.accentColor,
            opacity: 0.5,
            width: "100%",
          }}
        />
      </Box>
    </SnackbarContent>
  );
});
